import { CSSProperties, useMemo, useState } from "react";
import { useAppSelector } from "../../state/store";
import { Swiping } from "../../types/generalTypes";
import "./Card.scss";

interface CardProps {
  isFlipped: boolean;
  size: {
    height: number | string;
    width: number | string;
  };
  frontContent: React.ReactNode;
  frontStyle?: CSSProperties;
  backContent: React.ReactNode;
  backStyle?: CSSProperties;
  onClick: () => void; // Either flips the card, or selects it to do something else.
  className?: string;
  style?: CSSProperties;
  ariaLabel: string;
  cardsUnder?: number;
  allowSwipe?: boolean;
  oldContent?: {
    content: React.ReactNode;
    movement: "DECK" | "FLY";
    direction?: "left" | "right";
  };
  disableAnimation?: boolean;
  "data-testId"?: string;
}

/**
 * Card that can be flipped, has content on the front and back,
 */
export function Card(props: CardProps) {
  const settings = useAppSelector((state) => state.settings);

  const swipeStatusDefault: Swiping = {
    sX: -1,
    sY: -1,
    eX: -1,
    eY: -1,
  };
  const [swipeStatus, setSwipeStatus] = useState<Swiping>(swipeStatusDefault);
  const [swipeDegree, setSwipeDegree] = useState(0);
  const endSwipe = () => {
    setSwipeStatus(swipeStatusDefault);
    setSwipeDegree(0);
  };

  // Use props, or randomize otherwise
  const animationDirection = useMemo(
    () =>
      props.oldContent?.direction || (Math.random() < 0.5 ? "left" : "right"),
    [props.isFlipped, props.oldContent?.direction]
  );

  return (
    <div
      style={{ position: "relative" }}
      onTouchStart={(e) => {
        if (!settings.touchscreen || !props.allowSwipe || props.isFlipped)
          return;
        setSwipeStatus({
          ...swipeStatus,
          sX: e.touches[0].clientX,
          eX: e.touches[0].clientX,
          sY: e.touches[0].clientY,
          eY: e.touches[0].clientY,
        });
      }}
      onTouchMove={(e) => {
        if (!settings.touchscreen || !props.allowSwipe || props.isFlipped)
          return;
        const newSwipeStatus = {
          ...swipeStatus,
          eX: e.touches[0].clientX,
          eY: e.touches[0].clientY,
        };

        const xDistance = Math.abs(newSwipeStatus.sX - newSwipeStatus.eX);
        const newSwipeDegree = Math.round(xDistance * 1.1) + 10;
        if (newSwipeDegree > 120) {
          props.onClick();
          endSwipe();
        } else {
          setSwipeStatus(newSwipeStatus);
          setSwipeDegree(newSwipeDegree);
        }
      }}
      onTouchEnd={endSwipe}
    >
      {!!props.cardsUnder &&
        Array(props.cardsUnder)
          .fill(0)
          .map((_, i) => {
            const offset = i + 1;
            const position = -(offset * 7);
            return (
              <span
                key={offset}
                className="card"
                style={{
                  position: "absolute",
                  top: position,
                  left: position,
                  zIndex: (props.cardsUnder || 0) - offset,
                  ...props.size,
                }}
              >
                <span className="flip-inner">
                  <span className="flip-front">&nbsp;</span>
                  <span className="flip-back" style={props.backStyle}>
                    &nbsp;
                  </span>
                </span>
              </span>
            );
          })}
      {props.oldContent && !props.disableAnimation && (
        <span
          className={`card${props.isFlipped ? " flipped" : ""} ${
            props.className || ""
          } ${
            props.oldContent.movement === "DECK"
              ? `card-to-deck-${animationDirection}`
              : `card-fly-away-${animationDirection}`
          }`}
          onClick={props.onClick}
          style={{ ...props.style, ...props.size }}
        >
          <span className="flip-inner">
            <span className="flip-front">
              <span className="card-text" style={props.frontStyle}>
                {props.oldContent.content}
              </span>
            </span>
          </span>
        </span>
      )}
      <button
        className={`card${props.isFlipped ? " flipped" : ""} ${
          props.className || ""
        }`}
        aria-label={props.ariaLabel}
        onClick={props.onClick}
        style={{ ...props.style, ...props.size, zIndex: props.cardsUnder || 1 }}
        data-testid={props["data-testId"]}
      >
        <span
          className="flip-inner"
          style={{
            transform: !props.isFlipped
              ? `rotateY(${swipeDegree}deg)`
              : undefined,
            transition: `transform ${props.disableAnimation ? 0 : 0.7}s`,
          }}
        >
          {(!props.disableAnimation || !props.isFlipped) && (
            <span className="flip-front">
              <span className="card-text" style={props.frontStyle}>
                {props.frontContent}
              </span>
            </span>
          )}
          {(!props.disableAnimation || props.isFlipped) && (
            <span className="flip-back" style={props.backStyle}>
              <span className="card-text">{props.backContent}</span>
            </span>
          )}
        </span>
      </button>
    </div>
  );
}
